import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Divider, Empty, Spin, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';

import { Lineitem } from '../../../../interfaces/Ordel.Interface';
import { useGetOrderLineItemsQuery } from '../../../../redux/api/order-line-item.ts';
import { TABLET_CARD_GRID_STYLE } from '../../tabletStyles';
import ItemCard from '../ItemCard';
import {
	DEFAULT_QUEUE_VIEW,
	KittingQueueSummary,
	parseKittingPrioritization,
	parseKittingQueueView,
} from '../kittingClassification';

interface IProps {
	productionMethod?: string;
	buildingId?: string | null;
	onSummaryChange?: (summary?: KittingQueueSummary) => void;
}

const KittingQueueTable: React.FC<IProps> = ({
	productionMethod,
	buildingId,
	onSummaryChange,
}) => {
	const [dataSource, setDataSource] = useState<Lineitem[]>([]);
	const [showMoreButton, setShowMoreButton] = useState(true);
	const [pageNumber, setPageNumber] = useState(1);
	const [pagesToFetch, setPagesToFetch] = useState<number[]>([]);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const history = useNavigate();

	const queryParams = new URLSearchParams(location.search);
	const search = queryParams.get('search') || '';
	const urlPage = Number(queryParams.get('page') || 1);
	const kittingStatus = queryParams.get('kitting') || 'Pending';
	const queueView =
		kittingStatus === 'Completed'
			? DEFAULT_QUEUE_VIEW
			: parseKittingQueueView(queryParams.get('queue_view'));
	const prioritization = parseKittingPrioritization(
		queryParams.get('prioritization')
	);

	const prevStatusRef = useRef(kittingStatus);
	const [hideDataUntilFetchComplete, setHideDataUntilFetchComplete] =
		useState(false);

	const currentPageToFetch =
		pagesToFetch.length > 0 ? pagesToFetch[0] : pageNumber;

	const kittingQuery = useMemo(() => {
		const q: Record<string, string | number> = {
			page_size: 10,
			page: currentPageToFetch,
			kitting: kittingStatus,
			queue_view: queueView,
			prioritization,
		};
		if (search) {
			q.search = search;
		}
		if (productionMethod) {
			q.partsbadger_production_method = productionMethod;
		}
		if (buildingId) {
			q.building = buildingId;
		}
		return q;
	}, [
		search,
		kittingStatus,
		currentPageToFetch,
		productionMethod,
		buildingId,
		queueView,
		prioritization,
	]);

	const { data, isFetching, refetch } = useGetOrderLineItemsQuery(kittingQuery, {
		refetchOnFocus: true,
		refetchOnReconnect: true,
		refetchOnMountOrArgChange: true,
	});

	useEffect(() => {
		if (prevStatusRef.current !== kittingStatus) {
			prevStatusRef.current = kittingStatus;
			setHideDataUntilFetchComplete(true);
		}
	}, [kittingStatus]);

	useEffect(() => {
		if (!isFetching && hideDataUntilFetchComplete) {
			setHideDataUntilFetchComplete(false);
		}
	}, [isFetching, hideDataUntilFetchComplete]);

	useEffect(() => {
		if (isInitialLoad && urlPage > 1) {
			const pages = Array.from({ length: urlPage }, (_, i) => i + 1);
			setPagesToFetch(pages);
			setPageNumber(urlPage);
		}
		setIsInitialLoad(false);
	}, []);

	const prevFiltersRef = useRef({
		buildingId,
		kittingStatus,
		search,
		productionMethod,
		queueView,
		prioritization,
	});

	useEffect(() => {
		const prev = prevFiltersRef.current;
		if (
			prev.buildingId !== buildingId ||
			prev.kittingStatus !== kittingStatus ||
			prev.search !== search ||
			prev.productionMethod !== productionMethod ||
			prev.queueView !== queueView ||
			prev.prioritization !== prioritization
		) {
			setDataSource([]);
			setPageNumber(1);
			setShowMoreButton(true);
			setPagesToFetch([]);
			prevFiltersRef.current = {
				buildingId,
				kittingStatus,
				search,
				productionMethod,
				queueView,
				prioritization,
			};
			const params = new URLSearchParams(location.search);
			params.set('page', '1');
			history({ search: params.toString() }, { replace: true });
		}
	}, [
		buildingId,
		kittingStatus,
		search,
		productionMethod,
		queueView,
		prioritization,
	]);

	useEffect(() => {
		if (data?.queue_summary) {
			onSummaryChange?.(data.queue_summary);
		}
	}, [data, onSummaryChange]);

	useEffect(() => {
		if (!data?.results) return;
		if (hideDataUntilFetchComplete) return;

		const results = data.results;

		if (currentPageToFetch === 1) {
			setDataSource(results);
		} else {
			setDataSource((prev) => {
				const existing = new Set(prev.map((item) => item.id));
				const newResults = results.filter((r) => !existing.has(r.id));
				if (newResults.length === 0) return prev;
				return [...prev, ...newResults];
			});
		}

		if (pagesToFetch.length > 0) {
			if (!data.next || pagesToFetch.length === 1) {
				setPagesToFetch([]);
				setShowMoreButton(!!data.next);
			} else {
				setPagesToFetch((prev) => prev.slice(1));
			}
		} else {
			setShowMoreButton(!!data.next);
		}
	}, [data, hideDataUntilFetchComplete]);

	const onLoadMore = () => {
		const nextPage = pageNumber + 1;
		setPageNumber(nextPage);
		const params = new URLSearchParams(location.search);
		params.set('page', String(nextPage));
		history({ search: params.toString() });
	};

	const resetAndRefetch = () => {
		setDataSource([]);
		setPagesToFetch([]);
		setShowMoreButton(true);
		const params = new URLSearchParams(location.search);
		params.set('page', '1');
		history({ search: params.toString() }, { replace: true });
		if (pageNumber === 1) {
			refetch();
		} else {
			setPageNumber(1);
		}
	};

	const displayCards = hideDataUntilFetchComplete ? [] : dataSource;
	const summary = data?.queue_summary;
	const readyCount = summary?.ready ?? summary?.not_started;
	const elsewhere =
		queueView !== 'All' && summary
			? (
					queueView === 'NotStarted'
						? [
								{ label: 'in progress', count: summary.in_progress },
								{ label: 'late', count: summary.late },
							]
						: queueView === 'Late'
							? [
									{ label: 'in progress', count: summary.in_progress },
									{ label: 'ready', count: readyCount },
								]
							: [
									{ label: 'late', count: summary.late },
									{ label: 'ready', count: readyCount },
								]
				).filter((entry) => (entry.count ?? 0) > 0)
			: [];

	const showLoadMoreFooter = showMoreButton && displayCards.length > 0;
	const loadMoreBusy = isFetching && displayCards.length > 0;

	return (
		<>
			{isFetching && displayCards.length === 0 ? (
				<div style={{ textAlign: 'center', padding: 48 }}>
					<Spin size="large" />
				</div>
			) : displayCards.length === 0 ? (
				<Empty
					image={null}
					description={
						<div>
							<Typography.Title level={4}>
								{queueView === 'NotStarted' && !search
									? 'Nothing ready to kit'
									: 'No Items Ready for Kitting'}
							</Typography.Title>
							<Typography.Text data-testid="fastlane-kit-queue-empty-reason">
								{search
									? `No results found for "${search}"`
									: elsewhere.length > 0
										? `Every job here is ${elsewhere
												.map((entry) => `${entry.count} ${entry.label}`)
												.join(' and ')}. Switch views above to see why.`
										: queueView !== 'All'
											? 'Nothing in this view right now'
											: 'If you think your part should be listed here, please check that Ballooning, Material Selection, CAM Programming, and Tooling Ordering (only if "Additional Tooling Required" is true) are completed.'}
							</Typography.Text>
						</div>
					}
				/>
			) : (
				<div style={TABLET_CARD_GRID_STYLE}>
					{displayCards.map((item) => (
						<ItemCard
							key={item.id}
							item={item}
							buildingId={buildingId ?? undefined}
							refetch={resetAndRefetch}
						/>
					))}
				</div>
			)}

			{showLoadMoreFooter ? (
				<>
					<Divider style={{ margin: '24px 0 16px' }} plain>
						<Typography.Text type="secondary" style={{ fontSize: 13 }}>
							More in queue
						</Typography.Text>
					</Divider>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: 8,
							paddingBottom: 8,
						}}
					>
						<Button
							size="large"
							onClick={onLoadMore}
							loading={loadMoreBusy}
						>
							Load more
						</Button>
					</div>
				</>
			) : null}
		</>
	);
};

export default KittingQueueTable;
